import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import i18next from 'i18next';
import { putErrorNotification } from '@wix/da-shared-react/pkg/utils/saga';
import { getCurrentUserHasCore } from '@wix/da-shared-react/pkg/publicSession/selectors';
import {
  PapiDaUserProfileModuleCoverDeviationSaveSkin,
  PapiDaUserProfileProfileskinsList,
  PapiRequestDaUserProfileModuleCoverDeviationSaveSkin,
  PapiRequestDaUserProfileProfileskinsList,
} from '@wix/da-papi-types';
import { fetchStreamContents } from '@wix/da-shared-react/pkg/Stream/helpers';
import {
  selectors as streamSelectors,
  withOffset,
} from '@wix/da-shared-react/pkg/Stream';
import { getActiveSkin } from '../selectors/profileSkins';
import {
  papiSkinToFedSkin,
  ProfileSkin,
  profileSkinsStreamId,
} from '../../types/profileSkins';
import { isApiError, request, XhrResponse } from '@wix/da-shared-react/pkg/xhr';
import { API_BASE_PATH } from '../../constants';
import { runModuleInitActions } from './modules';
import { ModuleType } from '../../types/modules';
import {
  closeSkinSelector,
  fetchMoreProfileSkins,
  finishSaving,
  saveActiveSkin,
  saveSkin,
} from '../actions/profileSkins';
import {
  getModuleIdByModuleType,
  getModuleVersion,
} from '../selectors/modules';
import { getProfileOwnerUser } from '../selectors/users';

function* handleFetchProfileSkins() {
  const { offset, limit } = yield select(state => ({
    offset: withOffset.selectors.getNextOffset(state, profileSkinsStreamId),
    limit: streamSelectors.getItemsPerFetch(state, profileSkinsStreamId),
  }));
  yield fetchStreamContents<
    PapiRequestDaUserProfileProfileskinsList,
    PapiDaUserProfileProfileskinsList
  >({
    streamId: profileSkinsStreamId,
    url: '/profileskins/list',
    baseURL: API_BASE_PATH,
    params: { limit, offset },
    getStreamData: result => ({
      hasMore: !!result.hasMore,
      items: result.results.map(papiSkinToFedSkin),
      nextOffset: result.nextOffset,
    }),
  });
}

function* handleSaveActiveSkin() {
  const activeSkin: ProfileSkin = yield select(getActiveSkin);
  yield put(saveSkin(activeSkin));
}

function* handleSaveSkin(action) {
  const moduleId: number = yield select(
    getModuleIdByModuleType,
    ModuleType.COVER_DEVIATION
  );
  const { skin } = action.payload;
  const moduleVersion: number = yield select(getModuleVersion, moduleId);
  const currentUserHasCore: boolean = yield select(getCurrentUserHasCore);
  const { username: profileOwnerUsername } = yield select(getProfileOwnerUser);

  if (!currentUserHasCore) {
    yield put(closeSkinSelector());
    return;
  }

  if (
    moduleId === undefined ||
    moduleVersion === undefined ||
    !profileOwnerUsername
  ) {
    yield putErrorNotification(
      i18next.t('skin.selector.save.error.noModuleFatal')
    );
    yield put(finishSaving());
    return;
  }

  const data: PapiRequestDaUserProfileModuleCoverDeviationSaveSkin = {
    moduleid: moduleId,
    username: profileOwnerUsername,
    version: moduleVersion,
    profile_skin_template_name: skin.skinId,

    image_display_mode: skin?.skin?.imageDisplayMode,
    image_deviation_id: skin?.skin?.image?.deviationId,
    theme: skin?.skin?.baseTheme,
  };

  const response: XhrResponse<PapiDaUserProfileModuleCoverDeviationSaveSkin> =
    yield call(request, {
      baseURL: API_BASE_PATH,
      method: 'post',
      url: '/module/cover_deviation/save_skin',
      data,
    });
  if (!response || isApiError(response) || !response.moduleData) {
    yield putErrorNotification(
      i18next.t('skin.selector.save.error.saveFailed')
    );
    yield put(finishSaving());
    return;
  }

  if (response.moduleData) {
    yield call(runModuleInitActions, response);
  }

  yield put(closeSkinSelector());
}

export default function* rootProfileSkinsSaga() {
  yield all([
    takeLatest(fetchMoreProfileSkins, handleFetchProfileSkins),
    takeEvery(saveActiveSkin, handleSaveActiveSkin),
    takeEvery(saveSkin, handleSaveSkin),
  ]);
}
